import { useLiveQuery } from 'dexie-react-hooks';
import { db } from './db';
import type { Client, ComplianceDocument, Job, JobRecord, PestActivity, RodentPlan, Technician } from './types';

const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name, 'en-AU', { sensitivity: 'base' });

export function useClients(): Client[] {
  return useLiveQuery(async () => (await db.clients.toArray()).sort(byName), []) ?? [];
}

export function useClient(id: string | undefined): Client | null | undefined {
  // undefined while loading, null when the client does not exist.
  return useLiveQuery(async () => (id ? (await db.clients.get(id)) ?? null : null), [id]);
}

export function useJobs(): Job[] {
  return useLiveQuery(() => db.jobs.toArray(), []) ?? [];
}

export function useTechnicians(): Technician[] {
  return useLiveQuery(async () => (await db.technicians.toArray()).sort(byName), []) ?? [];
}

export function useJobRecords(): JobRecord[] {
  return useLiveQuery(async () => (await db.jobRecords.toArray()).sort((a, b) => b.createdAt.localeCompare(a.createdAt)), []) ?? [];
}

export function usePestActivity(): PestActivity[] {
  return useLiveQuery(() => db.pestActivity.toArray(), []) ?? [];
}

export function useRodentPlans(): RodentPlan[] {
  return useLiveQuery(async () => (await db.rodentPlans.toArray()).sort((a, b) => b.savedAt.localeCompare(a.savedAt)), []) ?? [];
}

export function useDocuments(): ComplianceDocument[] {
  return useLiveQuery(async () => (await db.documents.toArray()).sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || '')), []) ?? [];
}

/** Ids of records with changes on this device that have not reached the server yet. */
export function useUnsyncedKeys(): Set<string> {
  return useLiveQuery(async () => new Set((await db.outbox.toArray()).map((op) => op.entityKey)), []) ?? new Set();
}
